import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import ActionMenu from '../../components/ActionMenu/ActionMenu';
import ActionMenuItem from '../../components/ActionMenu/ActionMenuItem';
import Button from '../../components/Buttons/Button';
import DataTable from '../../components/DataTable/DataTable';
import { IColInfo } from '../../components/DataTable/Interfaces';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import SpinnerOverlay from "../../components/Spinner/SpinnerOverlay";
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import { BusinessPartnerTypeEnum } from '../../enums/BusinessPartnerTypeEnum';
import LicenseQueryModel from '../../models/LicenseQueryModel';
import DataService from "../../services/data.service";
import Unauthorized from '../Authentication/Unauthorized';
import License_popup_CreateLicense from './Popups/License_popup_CreateLicense';


interface ILicenseFilter {
    customerId?: number
    countryId?: number
    categoryId?: number
    brandId?: number
    activeOnly: boolean
    validOnly: boolean
    rememberFilter: boolean
}

const SearchLicenseView: React.FC = () => {
    const [formData, setFormData] = useState<ILicenseFilter>({ activeOnly: true, validOnly: true, rememberFilter: false, })
    const [tableData, setTableData] = useState<LicenseQueryModel[]>();
    const [showCreatePopup, setShowCreatePopup] = useState<boolean>(false);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { handleException } = useNotifications();

    const customers = useSelectListOptions('api/v1/selectlist/BusinessPartners?businessPartnerTypeId=' + BusinessPartnerTypeEnum.Customer, '-- Select customer');
    const countries = useSelectListOptions('api/v1/selectlist/Countries', '-- Select country');
    const categories = useSelectListOptions('api/v1/selectlist/ProductCategoryNameTrees?activeOnly=' + formData.activeOnly, '-- Select category');
    const brands = useSelectListOptions('api/v1/selectlist/BrandNameTrees?activeOnly=' + formData.activeOnly, '-- Select brand');

    useEffect(() => {
        const filterItem = localStorage.getItem("licenseFilter");
        if (filterItem !== null) {
            let filter: ILicenseFilter = JSON.parse(filterItem);
            setFormData(filter);
        }
        getTableData();
    }, [])

    const colInfos: IColInfo[] = [
        { key: true, dataProp: 'id', visible: false },
        { title: 'Name', dataProp: 'name', filterInfo: { filterType: 'Textbox' } },
        { title: 'Licensor', dataProp: 'licensor', filterInfo: { filterType: 'Textbox' } },
        { title: 'Licensee', dataProp: 'licensee', filterInfo: { filterType: 'Textbox' } },
        { title: 'Summary', dataProp: 'summary', filterInfo: { filterType: 'Textbox' } },
        { title: 'Regions', dataProp: 'regions', filterInfo: { filterType: 'Textbox' } },
        { title: 'Countries', dataProp: 'countries', width: 400, filterInfo: { filterType: 'Textbox' } },
        { title: 'Valid', dataProp: 'isValid', dataType: 'bool', width: 75, filterInfo: { filterType: 'CheckboxList' } },
        { title: 'Begin date', dataProp: 'beginDate', dataType: 'date' },
        { title: 'End date', dataProp: 'endDate', dataType: 'date' },
    ];

    function submit() {
        getTableData();
    }

    function getTableData() {
        setShowSpinner(true);

        if (formData.rememberFilter)
            localStorage.setItem('licenseFilter', JSON.stringify(formData));
        else
            localStorage.removeItem(`licenseFilter`);

        DataService
            .post<LicenseQueryModel[]>(`api/v1/Licenses/Search/`, formData)
            .then(response => {
                setShowSpinner(false);
                setTableData(response);
            })
            .catch(e => {
                setShowSpinner(false);
                handleException(e);
            });
    }

    function setDefaultCriteria() {
        let defaultCriteria = {
            activeOnly: true,
            validOnly: true,
            rememberFilter: formData?.rememberFilter ?? false,
        }
        setFormData({ ...defaultCriteria })
    }

    function onFieldChanged(name: string, value: string): void {
        formData[name] = value;
        setFormData(formData);
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'customerId', label: 'Customer', labelSize: 3 },
            { name: 'countryId', label: 'Country', labelSize: 3 },
            { name: 'categoryId', label: 'Category' },
            { name: 'brandId', label: 'Brand' },
            { name: 'activeOnly', label: 'Active licenses only', labelSize: 8 },
            { name: 'validOnly', label: 'Valid licenses only', labelSize: 8 },
            { name: 'rememberFilter', label: 'Remember search criteria', labelSize: 8 },
        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData);

    const { isAuthorizedFor } = useAuth();
    if (!isAuthorizedFor(ApplicationFunctions.ViewLicense))
        return (<Unauthorized />);

    const formActions =
        <ActionMenu title="Actions">
            <ActionMenuItem icon="fa fa-plus" iconVariant="success" title="Create new License" onClick={() => { setShowCreatePopup(true); }} appFunction={ApplicationFunctions.CreateLicense} width={240} />
        </ActionMenu>

    return (
        <div className='main-content'>
            <Form title='Licenses' actions={formActions} footer={(<>
                <ButtonBar offset={2}>
                    <Button text='Search' {...getSubmitButtonProps()} />
                    <Button text='Clear' type='button' onClick={setDefaultCriteria} />
                </ButtonBar>
            </>)}>
                <Container fluid>
                    <Row>
                        <Col md='4'>
                            <SelectListField {...getFieldProps('customerId')} options={customers} />
                            <SelectListField {...getFieldProps('countryId')} options={countries} />
                        </Col>
                        <Col md='6'>
                            <SelectListField {...getFieldProps('categoryId')} options={categories} />
                            <SelectListField {...getFieldProps('brandId')} options={brands} />
                        </Col>
                        <Col md='2'>
                            <SwitchField {...getFieldProps('activeOnly')} />
                            <SwitchField {...getFieldProps('validOnly')} />
                            <SwitchField {...getFieldProps('rememberFilter')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            <Container fluid>
                <Row>
                    <Col>
                        <DataTable
                            name='dtLicenses'
                            colInfos={colInfos}
                            tableData={tableData}
                            navigateTitle='License'
                            navigateUrl='/plm/Licenses/Details/'
                        />
                    </Col>
                </Row>
            </Container>
            {showCreatePopup && <License_popup_CreateLicense show={showCreatePopup} onHide={() => setShowCreatePopup(false)} />}
            {showSpinner && (<SpinnerOverlay />)}
        </div>
    );
}
export default SearchLicenseView;
